import { toTestType } from '@risksmart-app/domain/src/types/consts/test-type';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import { useGetTestResultById } from 'src/hooks/queries';
import TestResultForm from 'src/pages/controls/update/forms/TestResultForm';
import type { TestResultFormFieldsData } from 'src/pages/controls/update/forms/testResultSchema';
import { defaultValues } from 'src/pages/controls/update/forms/testResultSchema';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import type { UserOption } from 'src/schemas/global';

import { useMultiParentFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useInsertControlTestResult } from '@/hooks/mutations/test-result/useInsertControlTestResult';
import { useUpdateTestResult } from '@/hooks/mutations/test-result/useUpdateTestResult';
import { MUTATION_NO_OP } from '@/hooks/useMutationResultNotification';
import type { CreatedEntityDestination } from '@/utils/createWithFiles';
import { saveFilesForOptionalCreatedEntity } from '@/utils/createWithFiles';
import { assessmentResultsUrl } from '@/utils/urls';
type Props = {
  readonly: boolean;
  navigateToResults: boolean;
  parentId: string;
  controlTestId?: string;
  assessedItem?: ObjectWithContributors;
  id?: string;
  onDismiss?: (saved: boolean) => void;
  beforeFieldsSlot?: ReactNode;
  controlIds?: string[];
  header?: string;
};

const ConnectedControlTestResultForm: FC<Props> = (props) => {
  const Id = props.id;
  const saveScope = `${props.parentId}:${Id ?? 'create'}`;
  const createdResultRef = useRef<
    { scope: string; resultIds: string[] } | undefined
  >(undefined);
  const currentScopeRef = useRef<string | undefined>(saveScope);
  currentScopeRef.current = saveScope;
  useEffect(() => {
    currentScopeRef.current = saveScope;

    return () => {
      if (currentScopeRef.current === saveScope) {
        currentScopeRef.current = undefined;
      }
    };
  }, [saveScope]);
  if (createdResultRef.current?.scope !== saveScope) {
    createdResultRef.current = undefined;
  }
  const { data, loading: loadingTestResult } = useGetTestResultById({
    queryArgs: { testResultId: Id ?? '' },
    shouldSkip: !Id,
  });
  const testResult = data?.test_result[0];
  const { updateFiles } = useMultiParentFileUpdate();

  const { insertControlTestResult } = useInsertControlTestResult();
  const { updateTestResult: update } = useUpdateTestResult();

  const onSave = async (data: TestResultFormFieldsData) => {
    const currentSaveScope = saveScope;
    try {
      const { files, ...rest } = data;
      let resultIds = Id ? [Id] : (createdResultRef.current?.resultIds ?? []);
      if (testResult) {
        const { ParentControlIds: _, ...updateData } = rest;
        const result = await update({
          ...updateData,
          OriginalTimestamp: testResult.ModifiedAtTimestamp,
          Id: testResult.Id,
          ParentControlId: testResult.ParentControlId,
          CustomAttributeData: rest.CustomAttributeData || undefined,
          Submitter: rest.Submitter.value,
        });
        if (!result.updateTestResultApi?.Id) {
          throw new Error(
            'Records not updated. Record may have been updated by another user'
          );
        }
        resultIds = [testResult.Id];
      } else if (resultIds.length === 0) {
        const result = await insertControlTestResult({
          ...rest,
          ControlIds: rest.ParentControlIds.map((p) => p.value),
          AssessmentId: props.parentId,
          CustomAttributeData: rest.CustomAttributeData || undefined,
          Submitter: rest.Submitter.value,
        });
        resultIds = result.insertControlTestResult?.Ids ?? [];
        if (resultIds.length === 0) {
          throw new Error('Id not found');
        }
        if (currentScopeRef.current === currentSaveScope) {
          createdResultRef.current = {
            scope: currentSaveScope,
            resultIds,
          };
        }
      }
      const savedId = resultIds[0];
      if (!savedId) {
        throw new Error('Id not found');
      }

      const createdEntity: CreatedEntityDestination | undefined = !testResult
        ? { id: savedId, destination: assessmentResultsUrl(props.parentId) }
        : undefined;
      const saveFiles = () =>
        updateFiles({
          parentType: Parent_Type_Enum.TestResult,
          parentIds: resultIds,
          originalFiles: testResult?.files.map((f) => f.file),
          selectedFiles: files,
        });
      await saveFilesForOptionalCreatedEntity({ createdEntity, saveFiles });

      if (currentScopeRef.current !== currentSaveScope) {
        return MUTATION_NO_OP;
      }

      return {
        savedIds: resultIds,
        createdEntity,
        successNavigation: props.navigateToResults
          ? { destination: assessmentResultsUrl(props.parentId) }
          : undefined,
      };
    } catch (error) {
      if (currentScopeRef.current !== currentSaveScope) {
        return MUTATION_NO_OP;
      }
      throw error;
    }
  };

  const handleDismiss = (isSaved: boolean) => {
    if (currentScopeRef.current === saveScope) {
      props.onDismiss?.(isSaved);
    }
  };

  return (
    <div className={'pb-5'}>
      <TestResultForm
        {...props}
        renderTemplate={(renderProps) => <PageWrapper {...renderProps} />}
        defaultValues={{
          ...defaultValues,
        }}
        assessmentMode={'rating'}
        values={{
          ...defaultValues,
          ...testResult,
          TestType: toTestType(testResult?.TestType),
          files: testResult?.files.map((rf) => rf.file) ?? [],
          Submitter: testResult?.Submitter
            ? { value: testResult.Submitter, type: 'user' }
            : (undefined as unknown as UserOption),
          ParentControlIds: testResult
            ? [{ value: testResult.ParentControlId }]
            : props.controlIds
              ? props.controlIds.map((c) => ({
                  value: c,
                }))
              : [],
        }}
        onSave={onSave}
        onDismiss={handleDismiss}
        readOnly={props.readonly || loadingTestResult}
        disableControlSelect={props.id !== undefined}
      />
    </div>
  );
};

export default ConnectedControlTestResultForm;
